import { useQuery } from "react-query";
import { PensionContext, PensionParameters } from "../../contexts/pension-robot/PensionContext";
import { defaultHeaders } from "../robotapi";
import { useContext } from "react";
import { RobotEnvironmentContext } from "../../contexts/general/RobotEnvironmentContext";
import { buildEndpoint } from "../endpoints";
import { PensionDialogueDataResponse } from "./useGetPensionDialogueData";
import { isPensionCalculatedTogether } from "../../utils/functions";
import { PensionProgressSaverContext } from "../../components/pension-robot/cocktail-filler-screen/DialogueDataSaving/PensionProgressSaverContext";

export const pensionDialogueDataToRequest = (params: PensionParameters, userToken: string) : PensionDialogueDataResponse => {
    return {
        id: userToken,
        answers: {
            generalSituation: {
                age: params.personalInfo.self?.age,
                agePartner: isPensionCalculatedTogether(params.personalInfo) ? params.personalInfo.partner?.age : undefined,
                retirementAge: params.personalInfo.self?.pensionAge,
                retirementAgePartner: isPensionCalculatedTogether(params.personalInfo) ? params.personalInfo.partner?.pensionAge : undefined,
                workingStatus: params.personalInfo.self?.employmentStatus,
                workingStatusPartner: isPensionCalculatedTogether(params.personalInfo) ? params.personalInfo.partner?.employmentStatus : undefined,
                netMonthlyIncome: params.personalInfo.self?.salary,
                netMonthlyIncomePartner: isPensionCalculatedTogether(params.personalInfo) ? params.personalInfo.partner?.salary : undefined,
                partnerPresent: params.personalInfo.maritalStatus === 'married',
                yearsAbroad: params?.pensionInfo?.self?.isEmployedAbroad ? params.pensionInfo.self.yearsEmployedAbroad : undefined,
                yearsAbroadPartner: params?.pensionInfo?.partner?.isEmployedAbroad ? params.pensionInfo.partner.yearsEmployedAbroad : undefined
            },
            monthlyNetIncomeNeeded: params.expensesInfo?.goal,
            monthlyNetIncomeKnownAlready: true,
            pensionAow: {
                grossYearlyAow: params.personalInfo.maritalStatus === "married"
                    ? params.pensionInfo?.self?.aowGrossYearly?.together
                    : params.pensionInfo?.self?.aowGrossYearly?.single,
                grossYearlyAowPartner: params.pensionInfo?.partner?.aowGrossYearly,
                grossSecondPillarYearlyPension: params.pensionInfo?.self?.employeePensionGrossYearly,
                grossSecondPillarYearlyPensionPartner: params.pensionInfo?.partner?.employeePensionGrossYearly,
            },
            pensionAccounts: params.supplementInfo?.self?.accounts.map(acc => {
                return {
                        typeOfAdditionalPensionAccount: acc.type,
                        grossPaymentAmountOnInvestmentEndDate: acc.predictedAmountAtEndAccount,
                        investmentEndDate: acc.availableAt,
                        currentValue: acc.currentAmount,
                        paymentRecurringAmount: acc.recurringPaymentAmount,
                        paymentRecurringInterval: acc.recurringPaymentInterval,
                        riskProfileId: acc.type === "saving"
                            ? undefined
                            : acc.riskProfile
                }
            }),
            pensionAccountsPartner: params.supplementInfo?.partner?.accounts.map(acc => {
                return {
                        typeOfAdditionalPensionAccount: acc.type,
                        grossPaymentAmountOnInvestmentEndDate: acc.predictedAmountAtEndAccount,
                        investmentEndDate: acc.availableAt,
                        currentValue: acc.currentAmount,
                        paymentRecurringAmount: acc.recurringPaymentAmount,
                        paymentRecurringInterval: acc.recurringPaymentInterval,
                        riskProfileId: acc.type === "saving"
                            ? undefined
                            : acc.riskProfile
                }
            }),
            yearSpaceParameters: {
                pensionGrowthYearSpace: params.supplementInfo?.yearSpaceParams?.factorA,
                increaseOfFor: params.supplementInfo?.yearSpaceParams?.FORIncrease,
                decreaseOfFor: params.supplementInfo?.yearSpaceParams?.FORDecrease,
                grossYearIncomeYearSpace: params.supplementInfo?.yearSpaceParams?.grossYearlyIncome
            },
            yearSpacePensionAccount: {
                typeOfAdditionalPensionAccount: params.supplementInfo?.yearSpacePensionAccount?.typeOfAdditionalPensionAccount,
                riskProfileId: params.supplementInfo?.yearSpacePensionAccount?.typeOfAdditionalPensionAccount === "saving"
                    ? undefined    
                    : params.supplementInfo?.yearSpacePensionAccount?.riskProfileId,
                monthlyPaymentAmount: params.supplementInfo?.yearSpacePensionAccount?.monthlyPaymentAmount
            },
            box3InvestmentAccount: {
                paymentSingleAmount: params.supplementInfo?.self?.additionalInvestmentAccount?.isUsed 
                    ? params.supplementInfo?.self?.additionalInvestmentAccount?.account.currentAmount
                    : undefined,
                paymentRecurringInterval: params.supplementInfo?.self?.additionalInvestmentAccount?.isUsed 
                    ? params.supplementInfo?.self?.additionalInvestmentAccount?.account.recurringPaymentInterval
                    : undefined,
                paymentRecurringAmount: params.supplementInfo?.self?.additionalInvestmentAccount?.isUsed 
                    ? params.supplementInfo?.self?.additionalInvestmentAccount?.account.recurringPaymentAmount
                    : undefined,
                riskProfileId: params.supplementInfo?.self?.additionalInvestmentAccount?.account.riskProfile
            },
            box3SavingsAccount: {
                paymentSingleAmount: params.supplementInfo?.self?.additionalSavingAccount?.isUsed 
                    ? params.supplementInfo?.self?.additionalSavingAccount?.account.currentAmount
                    : undefined,
                paymentRecurringInterval: params.supplementInfo?.self?.additionalSavingAccount?.isUsed 
                    ? params.supplementInfo?.self?.additionalSavingAccount?.account.recurringPaymentInterval
                    : undefined,
                paymentRecurringAmount: params.supplementInfo?.self?.additionalSavingAccount?.isUsed 
                    ? params.supplementInfo?.self?.additionalSavingAccount?.account.recurringPaymentAmount
                    : undefined,
            },
            otherIncomeComponents: {
                giftAmountOnRetirementDate: params.supplementInfo?.otherIncomeAvailable ? params.supplementInfo?.otherIncome.gift : undefined,
                heritageAmountOnRetirementDate: params.supplementInfo?.otherIncomeAvailable ? params.supplementInfo?.otherIncome.heritage : undefined,
                surplusHouseAmountOnRetirementDate: params.supplementInfo?.otherIncomeAvailable ? params.supplementInfo?.otherIncome.surplusHouse : undefined,
                otherAmountOnRetirementDate: params.supplementInfo?.otherIncomeAvailable ? params.supplementInfo?.otherIncome.other : undefined
            }
        }
    };
};

const getUrl = () => {
    return buildEndpoint(['pensiondata', 'client']);
}

// Assumption, userId doesn't change in session
export const usePostDialogueData = () => {
    const { parameters } = useContext(PensionContext);
    const { userToken } = useContext(RobotEnvironmentContext);
    const {
        pensionProgressSaverData,
        setPensionProgressSaverData
    } = useContext(PensionProgressSaverContext);

    const onSuccess = () => {
        !!setPensionProgressSaverData && setPensionProgressSaverData({
            ...pensionProgressSaverData,
            dataIsSavedAtLeastOnce: true
        });
    };

    const query = useQuery(
        ['pensiondata', parameters],
        async () => {
            // If not tokenID, no requests should be made
            if (!userToken)
                return undefined;

            const request: RequestInit = {
                method: "POST",
                headers: defaultHeaders,
                body: JSON.stringify(pensionDialogueDataToRequest(parameters, userToken)),
            };
            const response = await fetch(getUrl(), request);

            if (!response.ok) {
                throw new Error(`Network error with response ${response}`);
            }

            return response;
        },
        {
            enabled: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            onSuccess
        }
    );

    return query;
}
